import { createContext, useContext, useMemo, useState, useEffect, useCallback } from "react";
import { X, Zap } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Global "Upgrade to Pro" banner triggered by any 402 API response.
 * Axios interceptor in api.js dispatches `window.dispatchEvent(new
 * CustomEvent('pro-required', { detail: { feature, message } }))` on
 * every 402; this provider listens for it and shows a persistent banner
 * across the entire authed console until the admin dismisses it or upgrades.
 */
const Ctx = createContext({ show: () => {}, hide: () => {}, current: null });

export function UpgradeBannerProvider({ children }) {
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    const handler = (e) => setCurrent(e.detail || { message: "This feature requires the Professional plan." });
    window.addEventListener("pro-required", handler);
    return () => window.removeEventListener("pro-required", handler);
  }, []);

  const show = useCallback((detail) => setCurrent(detail || {}), []);
  const hide = useCallback(() => setCurrent(null), []);

  const value = useMemo(() => ({ current, show, hide }), [current, show, hide]);
  return (
    <Ctx.Provider value={value}>
      {current && (
        <div className="sticky top-0 z-40 bg-warn border-b-4 border-ink" data-testid="upgrade-banner">
          <div className="max-w-7xl mx-auto flex items-center gap-4 px-4 py-3">
            <div className="p-2 border-2 border-ink bg-white shrink-0"><Zap className="w-4 h-4" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-black text-sm">
                {current.feature ? `${current.feature} · Pro plan required` : "Pro plan required"}
              </div>
              <div className="font-mono text-xs mt-0.5 text-ink/80 leading-relaxed">
                {current.message || "This feature is part of the Checkmate Professional plan. Upgrade to unlock — or use the 15-day grace access period after your last payment."}
              </div>
            </div>
            <Link
              to="/app/billing"
              onClick={hide}
              data-testid="upgrade-banner-cta"
              className="hard-btn px-4 py-2 font-mono uppercase text-xs tracking-widest whitespace-nowrap"
            >Upgrade Now</Link>
            <button
              data-testid="upgrade-banner-dismiss"
              onClick={hide}
              className="p-1.5 border-2 border-ink hover:bg-ink hover:text-white"
              aria-label="Dismiss"
            ><X className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}
      {children}
    </Ctx.Provider>
  );
}

export const useUpgradeBanner = () => useContext(Ctx);
